import React, { PropsWithChildren, useMemo } from "react";
import {
  TouchableOpacity,
  StyleProp,
  ViewStyle,
  TouchableOpacityProps,
  ViewProps,
  StyleSheet,
  View,
  GestureResponderEvent,
  TextStyle,
} from "react-native";

import { useTheme } from "../AHTheme/AHTheme";
import { useLayoutContext } from "../LayoutProvider";
import { AHIcon } from "../AHIcon";

interface AHListItemPropsTouchable extends TouchableOpacityProps {
  isTopItem?: boolean;
  isBottomItem?: boolean;
  onRequestDelete?: ((event: GestureResponderEvent) => void) | undefined;
  touchable?: true | undefined | null;
}

interface AHListItemPropsNonTouchable extends ViewProps {
  isTopItem?: boolean;
  isBottomItem?: boolean;
  onRequestDelete?: ((event: GestureResponderEvent) => void) | undefined;
  touchable: false;
}

export type AHListItemProps =
  | AHListItemPropsTouchable
  | AHListItemPropsNonTouchable;

const AHListItemStyle = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
  },
});

export const useAHListItemStyle = () => {
  const theme = useTheme();
  const { radius, space } = useLayoutContext();
  return useMemo(
    () => ({
      container: [
        AHListItemStyle.container,
        {
          padding: space.small,
          paddingVertical: space.medium,
          backgroundColor: theme.surface,
        },
      ],
      topItem: [
        {
          borderTopRightRadius: radius.medium,
          borderTopLeftRadius: radius.medium,
        },
      ],
      bottomItem: [
        {
          borderBottomRightRadius: radius.medium,
          borderBottomLeftRadius: radius.medium,
        },
      ],
    }),
    [radius.medium, space.medium, space.small, theme.surface]
  );
};

export const AHListItem = React.memo(
  React.forwardRef<typeof TouchableOpacity, PropsWithChildren<AHListItemProps>>(
    (props, ref) => {
      const liStyle = useAHListItemStyle();

      const style: StyleProp<ViewStyle> = useMemo(() => {
        const s: StyleProp<ViewStyle> = [
          AHListItemStyle.container,
          liStyle.container,
        ];
        if (props.isTopItem) {
          s.push(liStyle.topItem);
        }
        if (props.isBottomItem) {
          s.push(liStyle.bottomItem);
        }
        if (props.style) {
          s.push(props.style);
        }
        return s;
      }, [
        liStyle.container,
        liStyle.topItem,
        liStyle.bottomItem,
        props.isTopItem,
        props.isBottomItem,
        props.style,
      ]);

      const innerChild = useMemo(() => {
        if (props.touchable === false) {
          return (
            <View ref={ref as any} {...props} style={style}>
              {props.children}
            </View>
          );
        } else {
          return (
            <TouchableOpacity ref={ref as any} {...props} style={style}>
              {props.children}
            </TouchableOpacity>
          );
        }
      }, [props, ref, style]);

      return innerChild;
    }
  )
);

type ArrowProps = {
  color?: string;
  size?: number;
  style?: StyleProp<TextStyle>;
};

export const Arrow = ({ color, style }: ArrowProps) => {
  const theme = useTheme();
  return (
    <View style={style}>
      <AHIcon
        type="chevron-right"
        forceSize={20}
        customColor={color ?? theme.washedText}
    />
    </View>
  );
};

export const Plus = ({ color, style, size }: ArrowProps) => {
  const theme = useTheme();
  return (
    <View style={style}>
      <AHIcon
        type="add"
        forceSize={size ?? 17}
        customColor={color ?? theme.primary}
    />
    </View>
  );
};

const Cross = ({ color, style, size }: ArrowProps) => {
  const theme = useTheme();
  return (
    <View style={style}>
      <AHIcon
        type="clear"
        forceSize={size ?? 17}
        customColor={color ?? theme.darkText}
    />
    </View>
  );
};

type CheckProps = {
  selected?: boolean;
  color?: string;
};

const Check = (props: CheckProps) => {
  const theme = useTheme();
  const selectedColor = props.color ?? theme.primary;
  return (
    <View style={{ paddingRight: 10 }}>
      <AHIcon
        type="check"
        forceSize={20}
        customColor={props.selected ? selectedColor : "transparent"}
    />
    </View>
  );
};
